import { Column, Line } from "@ant-design/plots";
import { FolderKanban, Users, CheckCircle } from "lucide-react";

// Define types for data
interface ProjectData {
  month: string;
  completed: number;
  active: number;
}

interface ProductivityData {
  day: string;
  productivity: number;
}

// Static data for the charts
const projectData: ProjectData[] = [
  { month: "Jan", completed: 7, active: 5 },
  { month: "Feb", completed: 8, active: 6 },
  { month: "Mar", completed: 18, active: 12 },
  { month: "Apr", completed: 14, active: 10 },
  { month: "May", completed: 16, active: 13 },
  { month: "Jun", completed: 17, active: 15 },
];

// Transform the data to support multiple series (completed, active)
const transformedData = projectData.flatMap(item => [
  { month: item.month, type: "Completed", value: item.completed },
  { month: item.month, type: "Active", value: item.active },
]);

const productivityData: ProductivityData[] = [
  { day: "Mon", productivity: 45 },
  { day: "Tue", productivity: 50 },
  { day: "Wed", productivity: 55 },
  { day: "Thu", productivity: 48 },
  { day: "Fri", productivity: 43 },
];

const DashboardPage: React.FC = () => {
  // Project progress chart config
  const projectConfig = {
    data: transformedData,
    xField: "month",
    yField: "value",
    seriesField: "type",
    isGroup: true, // side-by-side bars

    colorField: "type", // Tell the chart to use 'type' for coloring
    color: ({ type }: { type: string }) => {
      return type === "Completed" ? "#1D4ED8" : "#F59E0B";
    },

    legend: {
      position: "top-left" as const,
    },
    label: {
      position: "middle",
      style: { fill: "#FFFFFF", fontWeight: "bold" },
    },
    tooltip: {
      shared: true,
      showMarkers: false,
    },
    columnStyle: {
      radius: [4, 4, 0, 0], // Rounded top
    },
  };

  // Employee productivity chart config
  const productivityConfig = {
    data: productivityData,
    xField: "day",
    yField: "productivity",
    point: { size: 5, shape: "diamond" },
    lineStyle: { lineWidth: 2, color: "#FF9900" },
    tooltip: {
      showMarkers: false,
      customContent: (title: any, items: any) => {
        const item = items[0];
        return (
          <div
            style={{
              padding: "10px",
              borderRadius: "4px",
              background: "#fff",
              border: "1px solid #e0e0e0",
            }}
          >
            <strong>{title}</strong>
            <div>Productivity: {item?.data?.productivity}</div>
          </div>
        );
      },
    },
  };

  return (
    <div className="container mx-auto">
      <div className="mb-4 mt-2">
        <span className="text-2xl font-bold text-[#111827]">Dashboard</span>
        <p className="text-sm text-[#627384]">
          Welcome back! Here&apos;s what&apos;s happening today.
        </p>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 md:grid-cols-3">
        <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
          <div>
            <h2 className="text-base font-semibold text-[#627384]">
              Total Projects
            </h2>
            <p className="text-2xl font-bold">48</p>
            <span className="text-green-500">↑ +12%</span>
          </div>
          <div className="rounded-full bg-[#0c3055] p-4">
            <FolderKanban className="text-white" /> {/* Lucide Icon */}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
          <div>
            <h2 className="text-base font-semibold text-[#627384]">
              Total Employees
            </h2>
            <p className="text-2xl font-bold">156</p>
            <span className="text-green-500">↑ +8%</span>
          </div>
          <div className="rounded-full bg-orange-400 p-4">
            <Users className="text-white" /> {/* Lucide Icon */}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
          <div>
            <h2 className="text-base font-semibold text-[#627384]">
              Active Projects
            </h2>
            <p className="text-2xl font-bold">32</p>
            <span className="text-green-500">↑ +5%</span>
          </div>
          <div className="rounded-full bg-green-600 p-4">
            <CheckCircle className="text-white" /> {/* Lucide Icon */}
          </div>
        </div>

        {/* <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow">
          <div>
            <h2 className="text-base font-semibold text-[#627384]">
              Hours Logged Today
            </h2>
            <p className="text-2xl font-bold">892</p>
            <span className="text-red-500">↓ -3%</span>
          </div>
          <div className="rounded-full bg-blue-600 p-4">
            <Clock className="text-white" />
          </div>
        </div> */}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-xl font-semibold">Project Progress</h3>
          <div style={{ height: "300px" }}>
            <Column {...projectConfig} />
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-xl font-semibold">Employee Productivity</h3>
          <div style={{ height: "300px" }}>
            <Line {...productivityConfig} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
